import {
  Button,
  ButtonColor,
  ButtonSize,
  Checkbox,
  Combobox,
  Loader,
  Option,
  TextInput,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Customer, CustomerDetailPageValidation } from 'src/front/api/apiInstance';
import { CardSelectionType, ReferenceDataType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import {
  dictionaryLineFormat,
  formatNumeric,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { CustomerDetailSchema } from 'src/front/common/utils/validationSchemas';

import {
  Container,
  Form,
  FormElement,
  FormRow,
  LineFormElement,
  Separator,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';

import { useCardTypeState } from 'src/front/contexts/CardEntryContext';

interface FormData {
  faceControl: boolean;
  maritalStatus: Option | null;
  numberOfChildren: string;
  educationStatus: Option | null;
  isGovernmentOfficer: boolean;
  hasRelativeForeignPublicOffical: Option | null;
  hasUSAPermit: Option | null;
  hasTravelPassport: Option | null;
  hasCEBLoanOfRelatives: Option | null;
}

const defaultValues = {
  faceControl: false,
  maritalStatus: null,
  numberOfChildren: '',
  educationStatus: null,
  isGovernmentOfficer: false,
  hasRelativeForeignPublicOffical: null,
  hasUSAPermit: null,
  hasTravelPassport: null,
  hasCEBLoanOfRelatives: null,
};

interface Props {
  customerId?: string;
  goToNextStep: (customerId?: string) => void;
  disablePage?: boolean;
}

const CustomerDetailEntry: FC<Props> = ({ customerId, goToNextStep, disablePage }) => {
  const { t } = useTranslation();
  const isCreditCard = useCardTypeState() === CardSelectionType.CREDITCARD;
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const maritalStatuses = usePRM(ReferenceDataType.MARITALSTATUS);
  const educationStatuses = usePRM(ReferenceDataType.EDUCATIONSTATUS);
  const yesNoComboItems = usePRM(ReferenceDataType.YESNOTYPE);

  const { handleSubmit, errors, setValue, register, watch, control, formState } = useForm<FormData>(
    {
      defaultValues,
      resolver: yupResolver(CustomerDetailSchema, {
        abortEarly: false,
        context: { isCreditCard },
      }),
    }
  );

  const loadDetailInformation = useCallback(async () => {
    const { data } = await CustomerDetailPageValidation.getCustomerDetailPageData();

    if (data.result === 'OK' && data.pageData) {
      const {
        maritalStatus,
        faceControl,
        educationStatus,
        hasRelativeForeignPublicOffical,
        hasUSAPermit,
        numberOfChildren,
        isGovernmentOfficer,
        hasTravelPassport,
        hasCEBLoanOfRelatives,
      } = data.pageData;
      [
        { name: 'faceControl', value: faceControl },
        { name: 'maritalStatus', value: optionFormat(maritalStatus) },
        { name: 'educationStatus', value: educationStatus ? optionFormat(educationStatus) : null },
        {
          name: 'hasRelativeForeignPublicOffical',
          value: optionFormat(hasRelativeForeignPublicOffical),
        },
        { name: 'hasUSAPermit', value: optionFormat(hasUSAPermit) },
        ...(isCreditCard
          ? [
              { name: 'numberOfChildren', value: `${numberOfChildren}` },
              { name: 'isGovernmentOfficer', value: isGovernmentOfficer },
              {
                name: 'hasTravelPassport',
                value: hasTravelPassport ? optionFormat(hasTravelPassport) : null,
              },
              {
                name: 'hasCEBLoanOfRelatives',
                value: hasCEBLoanOfRelatives ? optionFormat(hasCEBLoanOfRelatives) : null,
              },
            ]
          : []),
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    } else if (customerId) {
      const { data: response } = await Customer.getCustomerDetailInfo();

      const maritalStatus =
        maritalStatuses.find((status) => status.value === response.maritalStatusCode) || null;
      const educationStatus =
        educationStatuses.find((status) => status.value === response.educationStatusCode) || null;
      [
        { name: 'maritalStatus', value: maritalStatus },
        { name: 'educationStatus', value: educationStatus },
        ...(isCreditCard
          ? [{ name: 'numberOfChildren', value: `${response.numberOfChildren}` }]
          : []),
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    }

    setIsLoading(false);
  }, [customerId, educationStatuses, isCreditCard, maritalStatuses, setValue]);

  useEffect(() => {
    register({ name: 'faceControl' });
    register({ name: 'numberOfChildren' });
    register({ name: 'isGovernmentOfficer' });
    register({ name: 'hasTravelPassport' });
    register({ name: 'hasCEBLoanOfRelatives' });
  }, [register]);

  useEffect(() => {
    if (maritalStatuses.length && educationStatuses.length) {
      loadDetailInformation();
    }
  }, [customerId, educationStatuses.length, loadDetailInformation, maritalStatuses.length]);

  const onSubmit = async (form: FormData): Promise<void> => {
    const { data } = await CustomerDetailPageValidation.validateCustomerDetailPageData({
      faceControl: form.faceControl,
      maritalStatus: dictionaryLineFormat(form.maritalStatus),
      ...(form.educationStatus
        ? { educationStatus: dictionaryLineFormat(form.educationStatus) }
        : {}),
      hasRelativeForeignPublicOffical: dictionaryLineFormat(form.hasRelativeForeignPublicOffical),
      hasUSAPermit: dictionaryLineFormat(form.hasUSAPermit),
      ...(isCreditCard
        ? {
            numberOfChildren: Number(form.numberOfChildren),
            isGovernmentOfficer: form.isGovernmentOfficer,
            hasTravelPassport: dictionaryLineFormat(form.hasTravelPassport),
            hasCEBLoanOfRelatives: dictionaryLineFormat(form.hasCEBLoanOfRelatives),
          }
        : {}),
    });

    if (data.result === 'OK') {
      goToNextStep(customerId);
    }
  };

  const handleNumberChildrenChange = (text: string): void =>
    setValue('numberOfChildren', text, {
      shouldValidate: formState.isSubmitted || !!text,
      shouldDirty: formState.isSubmitted || !!text,
    });

  const handleComboChange = (name: string) => (option: Option | null) => {
    setValue(name, option, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_customerDetail_customerDetail_title" />
      </Title>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <Loader /> : null}
        <FormRow>
          <LineFormElement>
            <Checkbox
              disabled={disablePage}
              id="faceControl"
              label={t('cardEntry_customerDetail_faceControl_label')}
              onChange={({ target }) => setValue('faceControl', target.checked)}
              value={watch('faceControl')}
            />
          </LineFormElement>
        </FormRow>
        <Separator />
        <Subtitle>
          <Trans i18nKey="cardEntry_customerDetail_additionalInfo_subtitle" />
        </Subtitle>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="maritalStatus"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.maritalStatus as FieldError)?.message as string)}
                  items={maritalStatuses}
                  label={t('cardEntry_customerDetail_maritalStatus_label')}
                  name="maritalStatus"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </FormElement>
          {isCreditCard ? (
            <FormElement>
              <TextInput
                disabled={disablePage}
                errorMessage={t(errors?.numberOfChildren?.message as string)}
                formatter={formatNumeric}
                label={t('cardEntry_customerDetail_numberOfChildren_label')}
                maxLength={2}
                name="numberOfChildren"
                onChange={handleNumberChildrenChange}
                value={watch('numberOfChildren')}
                required
              />
            </FormElement>
          ) : null}
        </FormRow>

        <FormRow>
          <LineFormElement>
            <Controller
              control={control}
              name="educationStatus"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.educationStatus as FieldError)?.message as string)}
                  items={educationStatuses}
                  label={t('cardEntry_customerDetail_education_label')}
                  name="educationStatus"
                  onChange={onChange}
                  required={isCreditCard}
                  value={value}
                />
              )}
            />
          </LineFormElement>
        </FormRow>
        {isCreditCard ? (
          <FormRow>
            <FormElement>
              <Checkbox
                disabled={disablePage}
                id="isGovernmentOfficer"
                label={t('cardEntry_customerDetail_stateeEmployee_label')}
                onChange={({ target }) => setValue('isGovernmentOfficer', target.checked)}
                value={watch('isGovernmentOfficer')}
              />
            </FormElement>
          </FormRow>
        ) : null}
        <Separator />
        <FormRow>
          <Trans i18nKey="cardEntry_customerDetail_relavieForeignPublicOfficials_label" />
        </FormRow>
        <FormRow>
          <LineFormElement>
            <Controller
              control={control}
              name="hasRelativeForeignPublicOffical"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t(
                    (errors?.hasRelativeForeignPublicOffical as FieldError)?.message as string
                  )}
                  items={yesNoComboItems}
                  label={t('cardEntry_customerDetail_doYouConfirmPublicOfficials_label')}
                  name="hasRelativeForeignPublicOffical"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </LineFormElement>
        </FormRow>
        <Separator />
        <FormRow>
          <Trans i18nKey="cardEntry_customerDetail_questionaireUsa_label" />
        </FormRow>
        <FormRow>
          <LineFormElement>
            <Controller
              control={control}
              name="hasUSAPermit"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.hasUSAPermit as FieldError)?.message as string)}
                  items={yesNoComboItems}
                  label={t('cardEntry_customerDetail_doYouConfirm_label')}
                  name="hasUSAPermit"
                  onChange={onChange}
                  value={value}
                  required
                />
              )}
            />
          </LineFormElement>
        </FormRow>
        {isCreditCard ? (
          <>
            <Separator />
            <FormRow>
              <Trans i18nKey="cardEntry_customerDetail_validTravelPassport_label" />
            </FormRow>
            <FormRow>
              <LineFormElement>
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.hasTravelPassport as FieldError)?.message as string)}
                  items={yesNoComboItems}
                  label={t('cardEntry_customerDetail_doYouConfirm_label')}
                  name="hasTravelPassport"
                  onChange={handleComboChange('hasTravelPassport')}
                  value={watch('hasTravelPassport')}
                  required
                />
              </LineFormElement>
            </FormRow>
            <Separator />
            <FormRow>
              <Trans i18nKey="cardEntry_customerDetail_relativeHavingCEBLoan_label" />
            </FormRow>
            <FormRow>
              <LineFormElement>
                <Combobox
                  disabled={disablePage}
                  errorMessage={t((errors?.hasCEBLoanOfRelatives as FieldError)?.message as string)}
                  items={yesNoComboItems}
                  label={t('cardEntry_customerDetail_doYouConfirm_label')}
                  name="hasCEBLoanOfRelatives"
                  onChange={handleComboChange('hasCEBLoanOfRelatives')}
                  value={watch('hasCEBLoanOfRelatives')}
                  required
                />
              </LineFormElement>
            </FormRow>
          </>
        ) : null}
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </Form>
    </Container>
  );
};

export default CustomerDetailEntry;
